<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h2>{{ objectData.model.toUpperCase() }}: {{ objectData.name }}</h2>
      </v-col>
    </v-row>
    <v-row class="text-left">
      <v-col cols="6">
        <v-card>
          <column-chart
            ref="surveys"
            :library="{ title: 'Surveys With Responses' }"
            adapter="google"
            :data="surveyAndResponses"
            :messages="{ empty: 'No data' }"
            refresh="60"
          />
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-progress-linear
          v-if="loadingStakeholdersAndSurveys"
          indeterminate
          color="cyan"
        />
        <v-card
          :key="objectData.pk"
          style="height: 100%; padding: 5%"
        >
          <v-skeleton-loader
            :loading="loadingStakeholdersAndSurveys"
            type="article"
          >
            <h2><u>Number of Stakeholders and Surveys</u></h2><br>
            <h4>
              Total Stakeholders:
              {{ totalStakeholders }}
            </h4>
            <br>
            <h4>
              Total Surveys:
              {{ totalSurveys }}
            </h4>
          </v-skeleton-loader>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="text-left">
      <v-col cols="6">
        <v-progress-linear
          v-if="loadingConversations"
          indeterminate
          color="cyan"
        />

        <v-card
          :key="objectData.pk"
          style="height: 100%; padding: 5%"
        >
          <v-skeleton-loader
            :loading="loadingConversations"
            type="article"
          >
            <h2><u>Number of Conversations (with Percentages)</u></h2><br>
            <h4>
              Total Conversations:
              {{ totalConversations }}
            </h4>
            <br>
            <h4>
              Stakeholders with Conversations (%):
              {{ formattedConversationPercentage }}
            </h4>
          </v-skeleton-loader>
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-card>
          <area-chart
            ref="timestamps"
            :library="{ title: 'Survey Responses Over Time' }"
            adapter="google"
            :data="responseTimestamps"
            :messages="{ empty: 'No data' }"
            refresh="60"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
export default {
  name: "Analytics",
  props: {
    objectData: {
      type: Object,
      default: () => {
        return { pk: -1, name: "", model: "" }
      }
    }
  },
  data() {
    return {
      stakeholdersWithSurveysPoll: null,
      conversationsDataPoll: null,
      loadingStakeholdersAndSurveys: true,
      loadingConversations: true
    };
  },
  computed: {
    ...mapState({
      programStakeholdersWithSurveys: state => state.programs.programStakeholdersWithSurveys,
      programConversations: state => state.programs.programConversations,
      projectStakeholdersWithSurveys: state => state.projects.projectStakeholdersWithSurveys,
      projectConversations: state => state.projects.projectConversations
    }),
    surveyAndResponses() {
      return `${process.env.VUE_APP_DRF_API_URL}/${this.objectData.model}/get_survey_with_responses?pk=${this.objectData.pk}`;
    },
    responseTimestamps() {
      return `${process.env.VUE_APP_DRF_API_URL}/${this.objectData.model}/get_response_timestamps?pk=${this.objectData.pk}`;
    },
    totalStakeholders() {
      return this.objectData.model === 'project' ? this.projectStakeholdersWithSurveys.stakeholders : this.programStakeholdersWithSurveys.stakeholders
    },
    totalSurveys() {
      return this.objectData.model === 'project' ? this.projectStakeholdersWithSurveys.surveys : this.programStakeholdersWithSurveys.surveys
    },
    totalConversations() {
      return this.objectData.model === 'project' ? this.projectConversations.conversations : this.programConversations.conversations
    },
    conversationPercentage() {
      const percentage = this.objectData.model === 'project' ? this.projectConversations.percentage : this.programConversations.percentage
      return percentage ? percentage : 0
    },
    formattedConversationPercentage() {
      return isNaN(this.conversationPercentage) ? this.conversationPercentage : this.conversationPercentage.toFixed(2)
    }
  },
  mounted() {
    this.retrieveData(this.objectData)
  },
  beforeDestroy() {
    this.destroyRefresh()
  },
  methods: {
    ...mapActions({
      getProgramStakeholdersWithSurveys: "programs/getProgramStakeholdersWithSurveys",
      getProgramConversations: "programs/getProgramConversations",
      getProjectStakeholdersWithSurveys: "projects/getProjectStakeholdersWithSurveys",
      getProjectConversations: "projects/getProjectConversations"
    }),
    destroyRefresh() {
      this.$refs.surveys.chart.dataSource = null;
      this.$refs.timestamps.chart.dataSource = null;
      clearInterval(this.stakeholdersWithSurveysPoll);
      clearInterval(this.conversationsDataPoll );
    },
    retrieveData(payload) {
      if (payload.model === "program") {
        this.getProgramData(payload.pk)
      } else {
        this.getProjectData(payload.pk)
      }
    },
    getProgramData(pk) {
      // This hits an endpoint to get the number of stakeholders and surveys in a program
      this.getProgramStakeholdersWithSurveys(pk).then(() => {
        this.stakeholdersWithSurveysPoll = setInterval(() => {
          this.getProgramStakeholdersWithSurveys(pk);
        }, 60000);
        this.loadingStakeholdersAndSurveys = false;
      });
      // This hits an endpoint to get the number of conversations in a program
      this.getProgramConversations(pk).then(() => {
        this.conversationsDataPoll = setInterval(() => {
          this.getProgramConversations(pk);
        }, 60000);
        this.loadingConversations = false;
      });
    },
    getProjectData(pk) {
      // This hits an endpoint to get the number of stakeholders and surveys in a project
      this.getProjectStakeholdersWithSurveys(pk).then(() => {
        this.stakeholdersWithSurveysPoll = setInterval(() => {
          this.getProjectStakeholdersWithSurveys(pk);
        }, 60000);
        this.loadingStakeholdersAndSurveys = false;
      });
      // This hits an endpoint to get the number of conversations in a project
      this.getProjectConversations(pk).then(() => {
        this.conversationsDataPoll = setInterval(() => {
          this.getProjectConversations(pk);
        }, 60000);
        this.loadingConversations = false;
      });
    }
  }
};
</script>